import { Logo as SeekLogo } from '@seek/logo';
import { useTranslations } from '@vocab/react';
import { Box, Link } from 'braid-design-system';
import { atoms } from 'braid-design-system/css';
import React, { type ComponentProps } from 'react';

import { useAppContext } from '../../../../../components/AppContext';
import { useAnalyticsContext } from '../../../../../modules/analytics/AnalyticsContext';

import translations from './.vocab';
import SeekBusinessLogo from './resources/SeekBusinessLogo';

import * as styles from './Logo.css';

const WithLink = ({
  isMobileClient,
  children,
}: {
  children: React.ReactNode;
  isMobileClient?: boolean;
}) => {
  const { getClientHref } = useAppContext();
  const {
    analytics: { trackLink },
  } = useAnalyticsContext();

  return !isMobileClient ? (
    <Link
      className={atoms({
        display: 'flex',
        alignItems: 'center',
        marginX: 'xxsmall',
        height: 'full',
      })}
      href={getClientHref()}
      onClick={() =>
        trackLink({
          eventName: 'navigation_pressed',
          linkPosition: 'header',
          isThirdPartyLink: 'false',
          linkText: 'company logo',
        })
      }
    >
      {children}
    </Link>
  ) : (
    <>{children}</>
  );
};

const BrandLogo = React.memo(
  ({
    brand,
    site,
    size,
  }: Omit<ComponentProps<typeof SeekLogo>, 'variant'> & { site: string }) => {
    switch (site) {
      case 'seekBusiness':
        return <SeekBusinessLogo />;
      case 'seekAnz':
        return <SeekLogo size={size} brand={brand} />;
      case 'talent':
        return <SeekLogo size={size} brand="seek" variant="standard" />; // because the header is in 'brand' background
      default:
        return <SeekLogo size={size} brand="seek" />;
    }
  },
);

const Logo = ({ isIntercept = false }: { isIntercept?: boolean }) => {
  const { site, isMobileClient, brand = 'seek' } = useAppContext();
  const { t } = useTranslations(translations);
  const isHirer = site === 'talent';

  // standard is larger in this case
  const size =
    !isHirer || (isIntercept && isMobileClient) ? 'standard' : 'fill';

  // so we can test using label instead + better for screen readers
  const ariaLabel = () => {
    let brandLabel: string = brand;
    if (isHirer) {
      brandLabel = `${brand} ${t('employer')}`;
    } else if (site === 'seekBusiness') {
      brandLabel = 'seek business';
    }

    return `${brandLabel} logo`;
  };
  const label = ariaLabel();

  return (
    <WithLink isMobileClient={isMobileClient}>
      <Box aria-label={label} className={styles.logoContainer[size]}>
        <BrandLogo brand={brand} site={site} size={size} />
      </Box>
      {isHirer && <Box className={styles.employer}>{t('employer')}</Box>}
    </WithLink>
  );
};

export default Logo;
